import { ComponentPropsWithoutRef, FC, useCallback } from 'react';
import { InternetCardViewData } from '@modules/get-slash-internet-card/lib/internet-card-view-data';
import {
  ViewStack,
  ViewStackContext,
} from '@modules/get-slash-internet-card/components/dialog-views/view-stack';
import { Locale } from '@tuic/i18n';
import { TariffListing } from '@modules/get-slash-internet-card/components/dialog-views/booking-view/tariff-listing';
import {
  DocumentRenderer,
  renderTermsOfUseCheckboxText,
  TextRenderer,
} from '@modules/get-slash-internet-card/lib/utils';
import { GeneralView } from '@modules/get-slash-internet-card/components/dialog-views/general-view';
import { useClientTranslation } from '@lib/i18n/client';
import { GetSlashCredentials } from '@lib/third-parties/get-slash.types';
import { RefreshDialogHandler } from '../dialog-view-renderer';
import { documentToReactTree, documentToReactTreeInline } from '@tuic/contentful';

type Props = {
  locale: Locale;
  getSlashCredentials: GetSlashCredentials;
  useDebugEndpoints?: boolean;
  data: InternetCardViewData['bookingView'];
  termsOfUseCheckbox?: ComponentPropsWithoutRef<typeof TariffListing>['termsOfUseCheckbox'];
  textRenderer: TextRenderer;
  documentRenderer: DocumentRenderer;
  refreshDialog: RefreshDialogHandler;
};

export const BookingView: FC<Props> = props => {
  const {
    locale,
    getSlashCredentials,
    useDebugEndpoints,
    termsOfUseCheckbox,
    data,
    textRenderer,
    documentRenderer,
    refreshDialog,
  } = props;

  const { t } = useClientTranslation();

  const showTariffList = useCallback(
    (push: ViewStackContext['push']) => {
      push(
        <TariffListing
          locale={locale}
          useDebugEndpoints={useDebugEndpoints}
          getSlashCredentials={getSlashCredentials}
          tariffs={data?.tariffs}
          fallbackImages={data?.fallbackImages}
          refreshDialog={refreshDialog}
          termsOfUseCheckbox={termsOfUseCheckbox}
        />,
      );
    },
    [
      getSlashCredentials,
      locale,
      useDebugEndpoints,
      data?.tariffs,
      data?.fallbackImages,
      refreshDialog,
    ],
  );

  return (
    <ViewStack>
      {({ push }) => (
        <GeneralView
          title={textRenderer(data?.headline)}
          text={documentRenderer(data?.text)}
          buttons={[
            {
              children:
                data?.buttonLabels?.discoverTariffs ?? t('Tarife entdecken'),
              variant: 'primary',
              onClick: () => showTariffList(push),
            },
          ]}
        />
      )}
    </ViewStack>
  );
};
